import { useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  IconButton,
  Stack,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditableSelect from './EditableDisplayElements/EditableSelect';
import EditableText from './EditableDisplayElements/EditableText';
import EditableBoolean from './EditableDisplayElements/EditableBoolean';
import EditablePrice from './EditableDisplayElements/EditablePrice';
import PasswordDialog from './DialogElements/PasswordDialog';

function EditableDetailView({
  open,
  onClose,
  item,
  schema,
  onItemUpdate,
  title,
  apiOptions = {},
  onFieldUpdate,
  customRenderers = {},
}) {
  const [passwordDialog, setPasswordDialog] = useState({
    open: false,
    itemId: null,
  });

  const handlePasswordDialogOpen = useCallback(() => {
    setPasswordDialog({
      open: true,
      itemId: item._id,
    });
  }, [item._id]);

  const handlePasswordDialogClose = useCallback(() => {
    setPasswordDialog({
      open: false,
      itemId: null,
    });
  }, []);

  const handlePasswordSave = useCallback(
    password => {
      onItemUpdate(passwordDialog.itemId, { password });
    },
    [passwordDialog.itemId, onItemUpdate]
  );

  const handleFieldUpdate = useCallback(
    async (key, newValue, item) => {
      const result = await onFieldUpdate(key, newValue, item);
      return result;
    },
    [onFieldUpdate]
  );

  const renderField = useCallback(
    (key, fieldSchema) => {
      // Conditional visibility: hide a field when its showIf predicate (evaluated
      // against the current item) returns false.
      if (typeof fieldSchema.showIf === 'function' && !fieldSchema.showIf(item)) {
        return null;
      }

      if (fieldSchema.type === 'password') {
        return (
          <Box key={key} sx={{ mb: 2 }}>
            <Typography variant='subtitle2' color='text.secondary'>
              {fieldSchema.label}
            </Typography>
            <Box
              onClick={handlePasswordDialogOpen}
              sx={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                p: 1,
                borderRadius: 1,
                '&:hover': {
                  bgcolor: 'action.hover',
                },
                position: 'relative',
                width: '100%',
                minHeight: '40px',
              }}
            >
              <Typography sx={{ flex: 1 }}>••••••••</Typography>
            </Box>
          </Box>
        );
      }

      if (fieldSchema.type === 'select' || fieldSchema.type === 'multi-select') {
        return (
          <Box key={key} sx={{ mb: 2 }}>
            <Typography variant='subtitle2' color='text.secondary'>
              {fieldSchema.label}
            </Typography>
            <EditableSelect
              value={(() => {
                const raw = item[fieldSchema.api?.itemField ?? key];
                if (fieldSchema.type === 'multi-select') {
                  if (Array.isArray(raw)) {
                    return raw.map(v =>
                      v && typeof v === 'object' ? (v[fieldSchema.api?.idName] ?? v) : v
                    );
                  }
                  return [];
                } else {
                  // single select
                  if (raw && typeof raw === 'object')
                    return fieldSchema.api?.idName
                      ? raw[fieldSchema.api.idName]
                      : (raw.value ?? raw.label ?? '');
                  return raw ?? '';
                }
              })()}
              setValue={newValue => handleFieldUpdate(key, newValue, item)}
              isEditable={!fieldSchema.readOnly}
              type={fieldSchema.type}
              options={apiOptions[key] || fieldSchema.options || []}
              getOptions={fieldSchema.getOptions}
              parentItem={item}
              label={fieldSchema.label}
              isDetailView={true}
              placeholder={!fieldSchema.readOnly ? fieldSchema.placeholder : '-'}
              required={fieldSchema.required}
              showValidation={true}
              schema={fieldSchema}
            />
          </Box>
        );
      }

      if (fieldSchema.type === 'price') {
        return (
          <Box key={key} sx={{ mb: 2 }}>
            <Typography variant='subtitle2' color='text.secondary'>
              {fieldSchema.label}
            </Typography>
            <EditablePrice
              value={item[key]}
              setValue={newValue => handleFieldUpdate(key, newValue, item)}
              isEditable={!fieldSchema.readOnly}
              placeholder={fieldSchema.placeholder || '0,00'}
              required={fieldSchema.required}
              showValidation={true}
              schema={fieldSchema}
            />
          </Box>
        );
      }

      if (fieldSchema.type === 'milliseconds') {
        const raw = item[key];
        const minutes =
          typeof raw === 'number'
            ? raw / 60000
            : raw === null || raw === undefined || raw === ''
              ? null
              : Number(raw) / 60000;
        const minutesValue = Number.isFinite(minutes) ? minutes : null;
        const schemaForInput = { ...fieldSchema, type: 'number' };

        return (
          <Box key={key} sx={{ mb: 2 }}>
            <Typography variant='subtitle2' color='text.secondary'>
              {fieldSchema.label}
            </Typography>
            <EditableText
              value={minutesValue}
              setValue={newValue => {
                if (newValue === '' || newValue === null || newValue === undefined) {
                  return handleFieldUpdate(key, null, item);
                }
                const parsed = Number.parseFloat(String(newValue));
                if (!Number.isFinite(parsed)) return;
                return handleFieldUpdate(key, Math.round(parsed * 60000), item);
              }}
              isEditable={!fieldSchema.readOnly}
              type={'number'}
              displayValue={fieldSchema.displayValue}
              placeholder={fieldSchema.placeholder}
              schema={schemaForInput}
              required={fieldSchema.required}
              showValidation={true}
            />
          </Box>
        );
      }

      if (fieldSchema.type === 'boolean') {
        return (
          <Box key={key} sx={{ mb: 2 }}>
            <Typography variant='subtitle2' color='text.secondary'>
              {fieldSchema.label}
            </Typography>
            <EditableBoolean
              value={item[key]}
              setValue={newValue => handleFieldUpdate(key, newValue, item)}
              isEditable={!fieldSchema.readOnly}
              label={fieldSchema.label}
              required={fieldSchema.required}
              readOnly={fieldSchema.readOnly}
              schema={fieldSchema}
            />
          </Box>
        );
      }

      // ──────────────────────────────────────────────────────────────────────────
      // CUSTOM RENDERER
      // Allow custom renderers to be provided via options
      // ──────────────────────────────────────────────────────────────────────────
      if (customRenderers[fieldSchema.type] || customRenderers[key]) {
        const CustomRenderer = customRenderers[fieldSchema.type] || customRenderers[key];
        return (
          <Box key={key} sx={{ mb: 2 }}>
            <CustomRenderer
              value={item[key]}
              setValue={newValue => handleFieldUpdate(key, newValue, item)}
              isEditable={!fieldSchema.readOnly}
              label={fieldSchema.label}
              item={item}
              fieldSchema={fieldSchema}
              apiOptions={apiOptions}
            />
          </Box>
        );
      }

      // ──────────────────────────────────────────────────────────────────────────
      // OBJECT (nested, single)
      // Renders the inner schema fields inline and updates the full object on
      // ──────────────────────────────────────────────────────────────────────────
      if (fieldSchema.type === 'object') {
        const nestedSchema = fieldSchema.schema || {};
        const objectValue = item[key] || {};

        return (
          <Box key={key} sx={{ mb: 2 }}>
            <Typography variant='subtitle2' color='text.secondary' sx={{ mb: 1 }}>
              {fieldSchema.label}
            </Typography>
            {/* Render each sub-field like a normal editable field but update the
              whole object so the backend receives the nested structure */}
            {Object.entries(nestedSchema).map(([subKey, subSchema]) => {
              const currentVal = objectValue[subKey];

              const updateSubField = async newSubValue => {
                const newObjectValue = { ...objectValue, [subKey]: newSubValue };
                await handleFieldUpdate(key, newObjectValue, item);
              };

              if (subSchema.type === 'boolean') {
                return (
                  <Box key={subKey} sx={{ mb: 2 }}>
                    <Typography variant='subtitle2' color='text.secondary'>
                      {subSchema.label}
                    </Typography>
                    <EditableBoolean
                      value={currentVal}
                      setValue={updateSubField}
                      isEditable={!subSchema.readOnly}
                      label={subSchema.label}
                      required={subSchema.required}
                      readOnly={subSchema.readOnly}
                      schema={subSchema}
                    />
                  </Box>
                );
              }

              if (subSchema.type === 'select' || subSchema.type === 'multi-select') {
                return (
                  <Box key={subKey} sx={{ mb: 2 }}>
                    <Typography variant='subtitle2' color='text.secondary'>
                      {subSchema.label}
                    </Typography>
                    <EditableSelect
                      value={currentVal || (subSchema.type === 'multi-select' ? [] : '')}
                      setValue={updateSubField}
                      isEditable={!subSchema.readOnly}
                      type={subSchema.type}
                      options={apiOptions[subKey] || []}
                      label={subSchema.label}
                      placeholder={subSchema.placeholder}
                      required={subSchema.required}
                      showValidation={true}
                      schema={subSchema}
                    />
                  </Box>
                );
              }

              if (subSchema.type === 'price') {
                return (
                  <Box key={subKey} sx={{ mb: 2 }}>
                    <Typography variant='subtitle2' color='text.secondary'>
                      {subSchema.label}
                    </Typography>
                    <EditablePrice
                      value={currentVal}
                      setValue={updateSubField}
                      isEditable={!subSchema.readOnly}
                      placeholder={subSchema.placeholder || '0,00'}
                      required={subSchema.required}
                      showValidation={true}
                      schema={subSchema}
                    />
                  </Box>
                );
              }

              // Default (text, number, etc.)
              return (
                <Box
                  key={subKey}
                  sx={{ mb: 2, pl: 3, borderLeft: '2px solid', borderColor: 'divider' }}
                >
                  <Typography variant='subtitle2' color='text.secondary'>
                    {subSchema.label}
                  </Typography>
                  <EditableText
                    value={currentVal}
                    setValue={updateSubField}
                    isEditable={!subSchema.readOnly}
                    type={subSchema.type}
                    displayValue={subSchema.listDisplayValue || subSchema.displayValue}
                    placeholder={subSchema.placeholder}
                    schema={subSchema}
                    required={subSchema.required}
                    showValidation={true}
                  />
                </Box>
              );
            })}
          </Box>
        );
      }

      return (
        <Box key={key} sx={{ mb: 2 }}>
          <Typography variant='subtitle2' color='text.secondary'>
            {fieldSchema.label}
          </Typography>
          <EditableText
            value={item[key]}
            setValue={newValue => handleFieldUpdate(key, newValue, item)}
            isEditable={!fieldSchema.readOnly}
            type={fieldSchema.type}
            displayValue={fieldSchema.displayValue}
            placeholder={fieldSchema.placeholder}
            schema={fieldSchema}
            required={fieldSchema.required}
            showValidation={true}
          />
        </Box>
      );
    },
    [item, handleFieldUpdate, handlePasswordDialogOpen, apiOptions]
  );

  if (!item) return null;

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth>
        <DialogTitle>
          <Stack direction='row' justifyContent='space-between' alignItems='center'>
            <Typography variant='h6'>{title || 'Details'}</Typography>
            <IconButton onClick={onClose} size='small'>
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {Object.entries(schema).map(([key, fieldSchema]) => renderField(key, fieldSchema))}
          </Box>
          {item?._id != null && (
            <Typography
              component='div'
              variant='caption'
              color='text.disabled'
              sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}
            >
              _id: {String(item._id)}
            </Typography>
          )}
        </DialogContent>
      </Dialog>

      {/* Password Dialog */}
      <PasswordDialog
        open={passwordDialog.open}
        onClose={handlePasswordDialogClose}
        onSave={handlePasswordSave}
      />
    </>
  );
}

export default EditableDetailView;
